<template>
  <div class="search-filters">
    <div
      v-if="!smallScreen && filtersList"
      class="search-filters__desktop"
    >
      <div class="search-filters__desktop-list">
        <div class="container">
          <div>
            <span
              class="search-filters__desktop-results-label"
              v-text="resultsLabel"
            />
            <span
              class="search-filters__desktop-results-count"
              v-text="`${filtersList.count} ${resultsUnit}`"
            />
          </div>

          <search-filters-products-sorting
            :event-category="sortCtaDesktopLabel"
            :sort-cta-mobile-label="sortCtaMobileLabel"
            :sort-cta-desktop-label="sortCtaDesktopLabel"
            :sort-expand-icon-alt="sortExpandIconAlt"
            :sort-collapse-icon-alt="sortCollapseIconAlt"
          />
          <search-filters-select
            v-for="(filter, i) in filtersList.filters"
            v-show="displaySelects"
            :key="i"
            :collapse-icon-alt="collapseIconAlt"
            :event-category="eventCategory"
            :expand-icon-alt="expandIconAlt"
            :filter="filter"
            :filter-label="filterLabel"
            :is-search-page="isSearchPage"
            @selected-option="selectOption"
          />
        </div>
      </div>
      <div class="search-filters-results search-filters-results-filters">
        <div class="container">
          <button
            v-if="selectedProductsFilters.length"
            class="search-filters-results-clear-btn"
            @click="clearSelectedProductsFilters()"
            v-text="resetCtaLabel"
          />

          <span
            v-for="(filter, i) in selectedProductsFilters"
            :key="i"
            class="search-filters-results-selected"
          >
            {{ filter.title }}
            <button
              class="search-filters-results-selected--remove"
              @click="unselectProductsFiltersOption(filter)"
            >
              <span class="is-sr-only">{{ removeIconAlt }}</span>
            </button>
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="smallScreen && filtersList"
      class="search-filters__mobile"
    >
      <div class="search-filters__mobile-list">
        <div class="container">
          <search-filters-products-sorting
            :event-category="sortCtaMobileLabel"
            :sort-cta-mobile-label="sortCtaMobileLabel"
            :sort-cta-desktop-label="sortCtaDesktopLabel"
            :sort-expand-icon-alt="sortExpandIconAlt"
            :sort-collapse-icon-alt="sortCollapseIconAlt"
          />
          <button
            class="search-filters__mobile-refine"
            @click="toggleModal"
          >
            {{ refineCtaLabel }} ({{ selectedProductsFilters.length }})
          </button>
        </div>
      </div>

      <modal
        :hide-overflow="false"
        :open="openModal"
      >
        <template slot="heading">
          <div class="modal--heading">
            {{ refineTitle }}
          </div>
        </template>

        <div
          v-if="filtersList.filters.length"
          class="modal-filters"
        >
          <div class="search-filters-select-wrapper">
            <search-filters-select
              v-for="(filter, i) in filtersList.filters"
              :key="i"
              :event-category="eventCategory"
              :filter="filter"
              :initial-display="i === 0"
              :is-search-page="isSearchPage"
              @selected-option="selectOption"
            />
          </div>

          <div
            v-if="selectedProductsFilters.length"
            class="search-filters__mobile-ctas"
          >
            <button
              class="search-filters__mobile-ctas--btn search-filters__mobile-ctas--btn-reset"
              @click="clearSelectedProductsFilters()"
            >
              {{ resetCtaLabel }}
            </button>
            <button
              class="search-filters__mobile-ctas--btn search-filters__mobile-ctas--btn-apply"
              @click="toggleModal()"
            >
              {{ `${applyCtaLabel} ${resultsUnit} (${filtersList.count})` }}
            </button>
          </div>
        </div>

        <template slot="close">
          <button
            class="modal__close"
            @click="toggleModal"
          >
            <span class="is-sr-only">{{ closeIconAlt }}</span>
          </button>
        </template>
      </modal>

      <div
        v-if="selectedProductsFilters.length"
        class="search-filters-results search-filters-results-filters"
      >
        <div class="container">
          <button
            v-if="selectedProductsFilters.length"
            class="search-filters-results-clear-btn"
            @click="clearSelectedProductsFilters()"
            v-text="resetCtaLabel"
          />

          <span
            v-for="(filter, i) in selectedProductsFilters"
            :key="i"
            class="search-filters-results-selected"
          >
            {{ filter.title }}
            <button
              class="search-filters-results-selected--remove"
              @click="unselectProductsFiltersOption(filter)"
            >
              <span
                class="is-sr-only"
                v-text="removeIconAlt"
              />
            </button>
          </span>
        </div>
      </div>

      <div class="search-filters-results search-filters-results-counter">
        <div class="container container--main">
          <span>{{ `${filtersList.count} ${resultsUnit}` }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import SearchFiltersSelect from '@Feature/SearchFilters/code/Scripts/components/SearchFiltersSelect/SearchFiltersSelect.vue';
  import SearchFiltersProductsSorting from '@Feature/SearchFilters/code/Scripts/components/SearchFiltersProductsSorting/SearchFiltersProductsSorting.vue';
  import { mapActions, mapGetters } from 'vuex';
  import Modal from '@Feature/Modal/code/Scripts/components/Modal/Modal.vue';

  export default {
    name: 'SearchFiltersProducts',
    components: {
      SearchFiltersSelect,
      SearchFiltersProductsSorting,
      Modal
    },
    props: {
      displaySelects: {
        default: false,
        type: Boolean,
      },
      initialFilters: { type: Object, required: true },
      sorting: { type: Object, required: true },
      refineCtaLabel: { type: String, required: false, default: 'Refine' },
      refineTitle: { type: String, required: false, default: 'Refine Products' },
      closeIconAlt: { type: String, required: false, default: 'Close' },
      expandIconAlt: { type: String, required: false, default: 'Expand' },
      collapseIconAlt: { type: String, required: false, default: 'Collapse' },
      filterLabel: { type: String, required: false, default: 'Filter By:' },
      resultsLabel: { type: String, required: false, default: 'Results:' },
      resultsUnit: { type: String, required: false, default: 'Products' },
      removeIconAlt: { type: String, required: false, default: 'Remove Filter' },
      resetCtaLabel: { type: String, required: false, default: 'Clear All' },
      applyCtaLabel: { type: String, required: false, default: 'Show' },
      sortCtaMobileLabel: { type: String, required: false, default: 'Sort Mobile' },
      sortCtaDesktopLabel: { type: String, required: false, default: 'Sort Desktop' },
      sortExpandIconAlt: { type: String, required: false, default: 'Sort Expand Icon Alt' },
      sortCollapseIconAlt: { type: String, required: false, default: 'Sort Collapse Icon Alt' },
      eventCategory: { type: String, required: false, default: 'search' },
      isSearchPage: { type: Boolean, required: false, default: false }
    },
    data() {
      return {
        filtersList: null,
        smallScreen: !window.matchMedia('(min-width: 1280px)').matches,
        openModal: false
      };
    },
    computed: {
      ...mapGetters('searchFiltersStore', [
        'selectedProductsFilters',
        'productsFilters'
      ]),
    },
    watch: {
      productsFilters(newVal) {
        this.filtersList = newVal;
      }
    },
    mounted() {
      this.filtersList = this.initialFilters;
      this.setProductsFilters(this.filtersList);
      this.setProductsSorting(this.sorting);
    },
    methods: {
      ...mapActions('searchFiltersStore', [
        'setProductsFilters',
        'setProductsSorting',
        'selectProductsFiltersOption',
        'unselectProductsFiltersOption',
        'clearSelectedProductsFilters'
      ]),
      selectOption(option) {
        if (option.checked) {
          this.selectProductsFiltersOption({
            filterId: option.filterId,
            selectedOptionId: option.selectedOptionId
          });
        } else {
          this.unselectProductsFiltersOption({
            filterId: option.filterId,
            selectedOptionId: option.selectedOptionId
          });
        }
      },
      toggleModal() {
        this.openModal = !this.openModal;
      }
    }
  };
</script>
